import { gql } from "@apollo/client";

// Shared by CREATE_ACTIVITY/UPDATE_ACTIVITY so their return shape has one definition.
const ACTIVITY_AUDIT_FIELDS = gql`
  fragment ActivityAuditFields on Activity {
    uid
    createdBy
    organizationUid
    name
    day
  }
`;

// Shared by every query reading from the activity_super_admin_view (LIST_ACTIVITIES_SUPER_ADMIN,
// LIST_ACTIVITIES_FOR_USER, LIST_ACTIVITIES_FOR_CLASSROOMS all return this same type) so the field
// list only needs to be kept in sync in one place. LIST_ACTIVITIES_ADMIN reads a different view
// (ActivityAdminView) and can't share this fragment even though its shape happens to match today.
const ACTIVITY_SUPER_ADMIN_VIEW_FIELDS = gql`
  fragment ActivitySuperAdminViewFields on ActivitySuperAdminView {
    uid
    name
    day
    startTime
    placeUid
    endTime
    capacity
    remaining
    taken
    placeByPlaceUid {
      uid
      name
    }
    activityGroupsByActivityUid {
      nodes {
        groupByGroupUid {
          uid
          name
        }
      }
    }
    activityClassroomsByActivityUid {
      nodes {
        classroomByClassroomUid {
          uid
          name
          gradeUid
        }
      }
    }
    userByOrganizerUid {
      uid
      firstName
      lastName
    }
    periodByPeriodUid {
      uid
      name
    }
  }
`;

const CREATE_ACTIVITY = gql`mutation CreateActivity(
  $day: Day!, 
  $name: String!, 
  $capacity: Int!,
  $startTime: Time!, 
  $endTime: Time!,
  $organizerUid: UUID,
  $placeUid: UUID,
  $periodUid: UUID!) {
createActivity(
input: {
    activity: {
    startTime: $startTime,
    endTime: $endTime,
    name: $name,
    placeUid: $placeUid,
    periodUid: $periodUid,
    organizerUid: $organizerUid,
    capacity: $capacity,
    day: $day}}
) {
activity {
    ...ActivityAuditFields
}
}
}
${ACTIVITY_AUDIT_FIELDS}`;

const UPDATE_ACTIVITY = gql`mutation UpdateActivity(
    $activityUid: UUID!,
    $day: Day!,
    $name: String!,
    $capacity: Int!,
    $startTime: Time!,
    $endTime: Time!,
    $organizerUid: UUID,
    $placeUid: UUID,
    $periodUid: UUID!) {
    updateActivityByUid(input: {
      activityPatch: {
        startTime: $startTime,
        endTime: $endTime,
        name: $name,
        placeUid: $placeUid,
        periodUid: $periodUid,
        organizerUid: $organizerUid,
        capacity: $capacity,
        day: $day
      }, uid: $activityUid
    }) {
      activity {
        ...ActivityAuditFields
      }
    }
  }
  ${ACTIVITY_AUDIT_FIELDS}`;

const LIST_USER_BY_ACTIVITY = gql`query GetUsersByActivity($first: Int = 100, $offset: Int = 0, $activityUid: UUID!) {
  allUserActivities(
    first: $first,
    offset: $offset,
    condition: {activityUid: $activityUid}) {
    nodes {
      userByUserUid {
        uid
        firstName
        lastName
        classroomByClassroomUid {
          name
        }
      }
    }
  }
}`;

const LIST_ACTIVITIES_SUPER_ADMIN = gql`
query GetActivities($first: Int, $offset: Int = 0, $filter: ActivitySuperAdminViewFilter!) {
  allActivitySuperAdminViews(
    filter: $filter,
    first: $first,
    offset: $offset,
    orderBy: [DAY_ASC, START_TIME_ASC, NAME_ASC]) {
    nodes {
      ...ActivitySuperAdminViewFields
    }
    totalCount
  }
}
${ACTIVITY_SUPER_ADMIN_VIEW_FIELDS}`;

const LIST_ACTIVITIES_ADMIN = gql`
query GetActivities($first: Int = 100, $offset: Int = 0, $filter: ActivityAdminViewFilter!) {
  allActivityAdminViews(
    filter: $filter,
    first: $first,
    offset: $offset,
    orderBy: [DAY_ASC, START_TIME_ASC, NAME_ASC]) {
    nodes {
      uid
      name
      day
      startTime
      placeUid
      endTime
      capacity
      remaining
      taken
      placeByPlaceUid {
        uid
        name
      }
      activityGroupsByActivityUid {
        nodes {
          groupByGroupUid {
            uid
            name
          }
        }
      }
      activityClassroomsByActivityUid {
        nodes {
          classroomByClassroomUid {
            uid
            name
            gradeUid
          }
        }
      }
      userByOrganizerUid {
        uid
        firstName
        lastName
      }
      periodByPeriodUid {
        uid
        name
      }
    }
    totalCount
  }
}`;

const CREATE_ACTIVITY_GROUP = gql`mutation CreateActivityGroup($groupUid: UUID!, $activityUid: UUID!) {
  createActivityGroup(input: { activityGroup: { groupUid: $groupUid, activityUid: $activityUid } }) {
    clientMutationId
  }
}
`;

const CREATE_ACTIVITY_CLASSROOM = gql`mutation CreateActivityClassroom($classroomUid: UUID!, $activityUid: UUID!) {
  createActivityClassroom(input: { activityClassroom: { classroomUid: $classroomUid, activityUid: $activityUid } }) {
    clientMutationId
  }
}
`;

const DELETE_ACTIVITY = gql`mutation DeleteActivity($activityUid: UUID!) {
  deleteActivityByUid(input: { uid: $activityUid }){
    activity{
      uid
      name
    }
  }
}
`;

const DELETE_ACTIVITY_GROUP = gql`mutation DeleteActivityGroup($groupUid: UUID!, $activityUid: UUID!) {
  deleteActivityGroupByGroupUidAndActivityUid(input: { groupUid: $groupUid, activityUid: $activityUid }) {
    clientMutationId
  }
}
`;

const DELETE_ACTIVITY_CLASSROOM = gql`mutation DeleteActivityClassroom($activityUid: UUID!, $classroomUid: UUID!) {
  deleteActivityClassroomByClassroomUidAndActivityUid(input: { activityUid: $activityUid, classroomUid: $classroomUid }) {
    clientMutationId
  }
}
`;

const GET_ACTIVITY = gql`query GetActivity($activityUid: UUID!){
  activityByUid(uid: $activityUid){
    uid
    name
    userByOrganizerUid {
      firstName
      lastName
    }
    startTime
    endTime
    placeByPlaceUid {
      name
    }
  }
} `;

const COPY_ACTIVITY = gql`mutation CopyActivity($activityUid: UUID!, $periodUid: UUID!) {
  copyActivity(
    input: { auid: $activityUid, puid: $periodUid }
  ) {
    clientMutationId
  }
}
`;

const COUNT_ACTIVITIES = gql`query CountActivities ($period_uid : UUID) {
  allActivities(condition: {periodUid: $period_uid}) {
    totalCount
  }
}`;

const COUNT_USER_ACTIVITES = gql`query CountUserActivities($period_uid : UUID) {
  allActivities(condition: {periodUid: $period_uid}) {
    nodes {
      userActivitiesByActivityUid {
        totalCount
      }
    }
  }
}`;

const UNREGISTER_ACTIVITY_FOR_USER = gql`
mutation UnregisterActivityForUser ($userUid: UUID!, $activityUid: UUID!) {
    unregisterActivityForUser(input:{uUid: $userUid, aUid: $activityUid}) {
      clientMutationId
    }
  }  
`;

const REGISTER_ACTIVITY_FOR_USER = gql`
mutation RegisterActivityForUser ($userUid: UUID!, $activityUid: UUID!) {
    registerActivityForUser(input:{uUid: $userUid, aUid: $activityUid}) {
      clientMutationId
    }
  }  
`;

const FILL_ACTIVITY_FOR_CLASSROOMS = gql`mutation FillActivityForClassrooms ($activityUid: UUID!, $classroomUids: [UUID]!) {
  fillActivityForClassrooms(
    input: {auid: $activityUid, cuids: $classroomUids}
  ) {
    integer
  }
}
`;

const FILL_ACTIVITY_FOR_GROUPS = gql`mutation FillActivityForGroups ($activityUid: UUID!, $groupUids: [UUID]!) {
  fillActivityForGroups(
    input: {auid: $activityUid, guids: $groupUids}
  ) {
    integer
  }
}
`;

const MAX_TAKEN_ACTIVITY = gql`query MaxTakenActivity($filter: ActivitySuperAdminViewFilter!) {
  allActivitySuperAdminViews(orderBy: TAKEN_DESC, first: 1, filter: $filter) {
    nodes {
      taken
    }
  }
}`;

const MIN_TAKEN_ACTIVITY = gql`query MaxTakenActivity($filter: ActivitySuperAdminViewFilter!) {
  allActivitySuperAdminViews(orderBy: TAKEN_ASC, first: 1, filter: $filter) {
    nodes {
      taken
    }
  }
}`;

const MAX_REMAINING_ACTIVITY = gql`query MaxRemainingActivity($filter: ActivitySuperAdminViewFilter!) {
  allActivitySuperAdminViews(orderBy: REMAINING_DESC, first: 1, filter: $filter) {
    nodes {
      remaining
    }
  }
}`;

const MIN_REMAINING_ACTIVITY = gql`query MaxRemainingActivity($filter: ActivitySuperAdminViewFilter!) {
  allActivitySuperAdminViews(orderBy: REMAINING_ASC, first: 1, filter: $filter) {
    nodes {
      remaining
    }
  }
}`;

const MAX_TAKEN_ACTIVITY_ADMIN = gql`query MaxTakenActivityAdmin($filter: ActivityAdminViewFilter!) {
  allActivityAdminViews(orderBy: TAKEN_DESC, first: 1, filter: $filter) {
    nodes {
      taken
    }
  }
}`;

const MIN_TAKEN_ACTIVITY_ADMIN = gql`query MaxTakenActivityAdmin($filter: ActivityAdminViewFilter!) {
  allActivityAdminViews(orderBy: TAKEN_ASC, first: 1, filter: $filter) {
    nodes {
      taken
    }
  }
}`;

const MAX_REMAINING_ACTIVITY_ADMIN = gql`query MaxRemainingActivityAdmin($filter: ActivityAdminViewFilter!) {
  allActivityAdminViews(orderBy: REMAINING_DESC, first: 1, filter: $filter) {
    nodes {
      remaining
    }
  }
}`;

const MIN_REMAINING_ACTIVITY_ADMIN = gql`query MaxRemainingActivityAdmin($filter: ActivityAdminViewFilter!) {
  allActivityAdminViews(orderBy: REMAINING_ASC, first: 1, filter: $filter) {
    nodes {
      remaining
    }
  }
}`;

const GET_CONCURENT_ACTIVITY_FOR_ACTIVITY = gql`query GetConcurentActivityForActivity($activityUid: UUID!) {
  getConcurentActivityForActivity(aUid: $activityUid) {
    nodes {
      userByUserUid {
        uid
        lastName
        firstName
      }
      activityByActivityUid {
        uid
        name
        startTime
        endTime
      }
    }
  }
}`;

const GET_NB_CONCURENT_ACTIVITY_FOR_ACTIVITY = gql`query GetConcurentActivityForActivity($activityUid: UUID!) {
  getConcurentActivityForActivity(aUid: $activityUid) {
    totalCount
  }
}`;

const LIST_ACTIVITIES_FOR_USER = gql`query listActivitiesForUser($userUid: UUID!, $first: Int, $offset: Int = 0, $filter: ActivitySuperAdminViewFilter!) {
  listActivitiesForUser(
    uUid: $userUid
    first: $first,
    offset: $offset,
    filter: $filter,
    orderBy: [DAY_ASC, START_TIME_ASC, NAME_ASC]
  ) {
    nodes {
      ...ActivitySuperAdminViewFields
    }
    totalCount
  }
}
${ACTIVITY_SUPER_ADMIN_VIEW_FIELDS}`;

const LIST_ACTIVITIES_FOR_CLASSROOMS = gql`query ListActivitiesForClassrooms($classroomUids: [UUID]!, $first: Int, $offset: Int = 0, $filter: ActivitySuperAdminViewFilter!) {
  listActivitiesForClassrooms(
    cUids: $classroomUids
    first: $first,
    offset: $offset,
    filter: $filter,
    orderBy: [DAY_ASC, START_TIME_ASC, NAME_ASC]
    ) {
      nodes {
        ...ActivitySuperAdminViewFields
      }
      totalCount
  }
}
${ACTIVITY_SUPER_ADMIN_VIEW_FIELDS}`;

const GET_ACTIVITIES_DISTRIBUTION = gql`query GetActivitiesDistribution ($periodUid: UUID!) {
  getActivitiesDistribution(aPeriodUid: $periodUid) {
    nodes{
      nb
        startTime
        day
    }
  }
}
`;

export {
  CREATE_ACTIVITY,
  UPDATE_ACTIVITY,
  LIST_USER_BY_ACTIVITY,
  LIST_ACTIVITIES_SUPER_ADMIN,
  LIST_ACTIVITIES_ADMIN,
  CREATE_ACTIVITY_GROUP,
  CREATE_ACTIVITY_CLASSROOM,
  DELETE_ACTIVITY,
  DELETE_ACTIVITY_GROUP,
  DELETE_ACTIVITY_CLASSROOM,
  GET_ACTIVITY,
  COPY_ACTIVITY,
  COUNT_ACTIVITIES,
  COUNT_USER_ACTIVITES,
  UNREGISTER_ACTIVITY_FOR_USER,
  REGISTER_ACTIVITY_FOR_USER,
  FILL_ACTIVITY_FOR_CLASSROOMS,
  FILL_ACTIVITY_FOR_GROUPS,
  MAX_TAKEN_ACTIVITY,
  MIN_TAKEN_ACTIVITY,
  MAX_REMAINING_ACTIVITY,
  MIN_REMAINING_ACTIVITY,
  MAX_TAKEN_ACTIVITY_ADMIN,
  MIN_TAKEN_ACTIVITY_ADMIN,
  MAX_REMAINING_ACTIVITY_ADMIN,
  MIN_REMAINING_ACTIVITY_ADMIN,
  GET_CONCURENT_ACTIVITY_FOR_ACTIVITY,
  GET_NB_CONCURENT_ACTIVITY_FOR_ACTIVITY,
  LIST_ACTIVITIES_FOR_USER,
  LIST_ACTIVITIES_FOR_CLASSROOMS,
  GET_ACTIVITIES_DISTRIBUTION,
};
